import { type Database } from "@fitness-app/supabase";

import { type CalendlyEventPayload } from "../external/Calendly";

export type SalesMeetingEntity = Database["public"]["Tables"]["sales_meeting"]["Row"];

export enum SalesMeetingStatus {
  Scheduled = "scheduled",
  Rescheduled = "rescheduled",
  Canceled = "canceled",
  NoShow = "no_show",
  Attended = "attended",
}

export enum SalesMeetingSaleStatus {
  Yes = "tak",
  No = "nie",
  Waiting = "czekam",
  NotQualified = "nie kwalifikuje się",
}

export enum SalesMeetingCallStatus {
  Ok = "ok",
  Rescheduled = "przełożyła",
  NoShow = "nie pojawiła się",
  Canceled = "odwołała",
  Resigned = "zrezygnowała",
  Reactivation = "reaktywacja",
}

export type SalesMeetingCallAnalysisStatus =
  | "queued"
  | "skipped_short"
  | "transcribed"
  | "matched"
  | "analyzed"
  | "unmatched"
  | "failed";

export type SalesMeetingCallAnalysisFeedback = {
  summary: string;
  score: number;
  detailedAssessment?: string;
  teamMemberFeedback?: string;
  strengths: string[];
  risks: string[];
  recommendations: string[];
  missedQuestions: string[];
  nextCoachingStep: string;
};

/**
 * SMS reminder status markers set by sales reps (mobile SalesCRM "ptaszki").
 * `dayBefore`/`morning`/`hourBefore` map 1:1 to the three SMS templates;
 * `noAnswer` is a manual flag. A `null`/absent value means "not set".
 */
export type SalesMeetingSmsReminderKind = "dayBefore" | "morning" | "hourBefore" | "noAnswer";

export type SalesMeetingSmsReminderMark = { sentAt: string; by: string };
export type SalesMeetingSmsNoAnswerMark = { markedAt: string; by: string };

/**
 * TRE-2125: funnel stages reported to Meta's Conversions API for CRM. Meta
 * takes the stage name as the event name (free-form), and asks for an event on
 * every lead status change — a negative outcome is a stage like any other.
 * ASCII on purpose: these become event names in Events Manager.
 */
export enum SalesMeetingMetaLeadStage {
  MeetingBooked = "MeetingBooked",
  MeetingAttended = "MeetingAttended",
  NotQualified = "NotQualified",
  Sale = "Sale",
}

/**
 * Meta integration id -> stage -> ISO timestamp it was reported at. Kept per
 * integration so one switched on later still gets the stages an earlier one
 * already took. Sent stages are never re-sent to the same integration.
 */
export type SalesMeetingMetaLeadStagesSent = Record<string, Partial<Record<SalesMeetingMetaLeadStage, string>>>;

export type SalesMeetingSmsReminders = {
  dayBefore?: SalesMeetingSmsReminderMark | null;
  morning?: SalesMeetingSmsReminderMark | null;
  hourBefore?: SalesMeetingSmsReminderMark | null;
  noAnswer?: SalesMeetingSmsNoAnswerMark | null;
};

export type SalesMeeting = Omit<
  SalesMeetingEntity,
  | "calendlyPayload"
  | "questionsAndAnswers"
  | "tracking"
  | "callAnalysisFeedback"
  | "callTranscriptSegments"
  | "smsReminders"
  | "metaLeadStagesSent"
> & {
  status: SalesMeetingStatus;
  saleStatus: SalesMeetingSaleStatus | null;
  callStatus: SalesMeetingCallStatus | null;
  callAnalysisStatus: SalesMeetingCallAnalysisStatus | null;
  callAnalysisFeedback: SalesMeetingCallAnalysisFeedback | null;
  callTranscriptSegments: unknown[] | null;
  calendlyPayload: CalendlyEventPayload | null;
  questionsAndAnswers: CalendlyEventPayload["questions_and_answers"] | null;
  tracking: CalendlyEventPayload["tracking"] | null;
  smsReminders: SalesMeetingSmsReminders;
  metaLeadStagesSent: SalesMeetingMetaLeadStagesSent;
};
